import { useRef } from "react";
import { formatDisplayDate } from "../utils/dateFormat";

function fmtMonto(v) {
  return `$${Number(v || 0).toFixed(2)}`;
}

function buildTimeEnd(hora, duracion_min) {
  if (!hora) return "";
  const [h, m] = hora.split(":").map(Number);
  const totalMin = h * 60 + m + (duracion_min || 60);
  const fh = Math.floor(totalMin / 60) % 24;
  const fm = totalMin % 60;
  return `${String(fh).padStart(2, "0")}:${String(fm).padStart(2, "0")}`;
}

export default function ComprobanteCierre({ detalle, onCerrar }) {
  const printRef = useRef(null);
  const egresos = (detalle.movimientos || []).filter((m) => m.tipo === "egreso");

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="comprobante-overlay" role="dialog" aria-modal="true" aria-label="Comprobante de cierre diario">
      <div className="comprobante-modal panel" ref={printRef}>
        {/* Acciones — se ocultan al imprimir */}
        <div className="comprobante-actions no-print">
          <button type="button" className="secondary-button edit-action" onClick={handlePrint}>
            Imprimir / Guardar PDF
          </button>
          <button type="button" className="secondary-button" onClick={onCerrar}>
            Cerrar
          </button>
        </div>

        {/* Encabezado */}
        <header className="comprobante-header">
          <h2 className="comprobante-title">Comprobante de Cierre Diario</h2>
          <p className="comprobante-fecha">{formatDisplayDate(detalle.fecha)}</p>
          {detalle.cerrado && (
            <p className="comprobante-meta">
              Cerrado por <strong>{detalle.cerrado_por_username}</strong>
              {detalle.cerrado_en ? ` · ${detalle.cerrado_en.replace("T", " ").slice(0, 16)}` : ""}
            </p>
          )}
          {!detalle.cerrado && (
            <p className="comprobante-meta status-warn">Vista previa — día aún abierto</p>
          )}
        </header>

        {/* Saldos */}
        <section className="comprobante-section">
          <h3 className="comprobante-section-title">Resumen de caja</h3>
          <table className="comprobante-table">
            <tbody>
              <tr>
                <td>Saldo apertura</td>
                <td className="comprobante-monto">{fmtMonto(detalle.saldo_apertura)}</td>
              </tr>
              <tr>
                <td>Ingresos del día</td>
                <td className="comprobante-monto status-ok">{fmtMonto(detalle.ingresos)}</td>
              </tr>
              <tr>
                <td>Egresos del día</td>
                <td className="comprobante-monto status-danger">{fmtMonto(detalle.egresos)}</td>
              </tr>
              <tr className="comprobante-total-row">
                <td><strong>Saldo de cierre</strong></td>
                <td className={`comprobante-monto ${(detalle.saldo_cierre || 0) >= 0 ? "status-ok" : "status-danger"}`}>
                  <strong>{fmtMonto(detalle.saldo_cierre)}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* Reservas */}
        <section className="comprobante-section">
          <h3 className="comprobante-section-title">
            Reservas del día ({(detalle.reservas || []).length})
          </h3>
          {(detalle.reservas || []).length === 0 ? (
            <p className="comprobante-empty">Sin reservas registradas para esta fecha.</p>
          ) : (
            <div className="report-table-wrap">
              <table className="report-table comprobante-table-full">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Cliente</th>
                    <th>Cancha</th>
                    <th>Horario</th>
                    <th>Estado</th>
                    <th>Monto</th>
                    <th>Pago</th>
                  </tr>
                </thead>
                <tbody>
                  {(detalle.reservas || []).map((r, i) => (
                    <tr key={r.id}>
                      <td>{i + 1}</td>
                      <td>{r.cliente}</td>
                      <td>{r.cancha}</td>
                      <td>{r.hora} – {buildTimeEnd(r.hora, r.duracion_min)}</td>
                      <td>
                        <span className={
                          r.estado === "activa" ? "status-ok"
                          : r.estado === "cancelada" ? "status-danger"
                          : "status-warn"
                        }>
                          {r.estado}
                        </span>
                      </td>
                      <td>{fmtMonto(r.monto)}</td>
                      <td>{r.pagado ? <span className="status-ok">Pagado</span> : <span className="status-warn">Pendiente</span>}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="comprobante-total-row">
                    <td colSpan={5}><strong>Total reservas</strong></td>
                    <td colSpan={2} className="comprobante-monto status-ok">
                      <strong>
                        {fmtMonto((detalle.reservas || []).reduce((acc, r) => acc + Number(r.monto || 0), 0))}
                      </strong>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </section>

        {egresos.length > 0 && (
          <section className="comprobante-section">
            <h3 className="comprobante-section-title">Egresos de caja ({egresos.length})</h3>
            <div className="report-table-wrap">
              <table className="report-table comprobante-table-full">
                <thead>
                  <tr><th>Concepto</th><th>Monto</th></tr>
                </thead>
                <tbody>
                  {egresos.map((m) => (
                    <tr key={m.id}>
                      <td>{m.concepto}</td>
                      <td className="comprobante-monto status-danger">{fmtMonto(m.monto)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="comprobante-total-row">
                    <td><strong>Total egresos</strong></td>
                    <td className="comprobante-monto status-danger">
                      <strong>{fmtMonto(egresos.reduce((a, m) => a + Number(m.monto || 0), 0))}</strong>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>
        )}

        <footer className="comprobante-footer no-print">
          <p>Control de Canchas — Documento generado el {new Date().toLocaleString("es")}</p>
        </footer>
      </div>
    </div>
  );
}
